import {
  Fragment,
  InputHTMLAttributes,
  PropsWithChildren,
  ReactNode,
} from 'react';
import { twMerge } from 'tailwind-merge';
import { VariantProps } from 'tailwind-variants';
import { Flex } from '../../../../layout';
import { Divider, IsVisible } from '../../../../utils';
import { FieldBase, IFieldBaseProps } from '../../Base';
import { checkboxStyles } from '../styles';
import { CheckboxFieldIcon, ICheckboxFieldIconProps } from '../utils/Icon';

export interface ICheckboxGroupFieldOptions<T = number | string> {
  children?: ReactNode;
  imageUrl?: string;
  label?: string;
  value: T;
}

type ICheckboxGroupFieldValue<T = number | string> = T;

export interface ICheckboxGroupFieldProps
  extends Omit<
      InputHTMLAttributes<HTMLInputElement>,
      'name' | 'type' | 'value'
    >,
    VariantProps<typeof checkboxStyles.input> {
  containerProps?: {
    className?: string;
    rest?: Omit<IFieldBaseProps, 'className'>;
  };
  dividerProps?: {
    hide?: boolean;
    orientation?: 'horizontal' | 'vertical';
  };
  groupContainerProps?: {
    className?: string;
    rest?: Omit<IFieldBaseProps, 'className'>;
  };
  iconProps?: ICheckboxFieldIconProps;
  labelProps?: {
    bold?: boolean;
    className?: string;
    color?: string;
    italic?: boolean;
    underline?: boolean;
  };
  name: string;

  options: ICheckboxGroupFieldOptions[];
  orientation?: 'horizontal' | 'vertical';
  shouldInjectAsHTML?: boolean;
  value: ICheckboxGroupFieldValue[];
}

export function CheckboxGroupField({
  children,
  className,
  containerProps,
  design,
  disabled = false,
  dividerProps = {
    hide: false,
    orientation: 'horizontal',
  },
  groupContainerProps,
  iconProps,
  inputSize = 'md',
  labelProps,
  name,
  onChange,
  options,
  orientation = 'vertical',
  readOnly = false,
  shouldInjectAsHTML = false,
  value,
  ...rest
}: PropsWithChildren<ICheckboxGroupFieldProps>) {
  return (
    <FieldBase
      className={twMerge('px-0', containerProps?.className)}
      iconPosition="right"
      {...containerProps?.rest}
    >
      <Flex
        className={checkboxStyles.groupContainer({
          orientation,
          className: groupContainerProps?.className,
        })}
        {...groupContainerProps?.rest}
      >
        {options.map(option => (
          <Fragment key={`${name}-${option.value}`}>
            <label
              className={checkboxStyles.label({
                design,
                disabled: disabled || readOnly,
                errored: !!containerProps?.rest?.error,
                className: labelProps?.className,
                bold: labelProps?.bold,
                italic: labelProps?.italic,
                underline: labelProps?.underline,
              })}
              htmlFor={`${name}-${option.value}`}
              style={{
                color: labelProps?.color,
              }}
            >
              <input
                className={checkboxStyles.input({
                  design,
                  errored: !!containerProps?.rest?.error,
                  disabled: disabled || readOnly,
                  inputSize,
                  className,
                })}
                defaultChecked={value.includes(option.value)}
                disabled={disabled}
                id={`${name}-${option.value}`}
                name={name}
                onChange={onChange}
                readOnly={readOnly}
                type="checkbox"
                value={option.value}
                {...rest}
              />

              <IsVisible when={!!option.children}>{children}</IsVisible>
              <IsVisible when={!!option.label}>
                <IsVisible when={!shouldInjectAsHTML}>
                  <span className="flex-1">{option.label}</span>
                </IsVisible>
                <IsVisible when={!!shouldInjectAsHTML}>
                  <span
                    className="flex-1"
                    dangerouslySetInnerHTML={{
                      __html: option.label as string,
                    }}
                  />
                </IsVisible>
              </IsVisible>
              <CheckboxFieldIcon
                className={iconProps?.className}
                design={design}
                disabled={disabled || readOnly}
                rest={iconProps?.rest}
                size={inputSize}
              />
            </label>

            <IsVisible when={!dividerProps.hide}>
              <Divider
                className="bg-zinc-200 my-0"
                orientation={dividerProps.orientation}
              />
            </IsVisible>
          </Fragment>
        ))}
      </Flex>
    </FieldBase>
  );
}
